import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useUser from '@/hooks/useUser';
import { FreelancerVacationBalanceResource } from '@/types/resources';
import { api } from '@/utils/api';
import { VacationRequestType } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';

type FormDataT = {
  freelancer_id: number;
  request_date: string;
  start_date: string;
  end_date: string;
  notes?: string;
  type: VacationRequestType;
};

const RemainingDaysNotice = styled.div`
  padding: 16px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 24px;
`;

const Days = styled(Text)`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Dot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.success};
`;

interface Props {
  balance?: FreelancerVacationBalanceResource;
  onClose: () => void;
  type: VacationRequestType;
}

const VacationRequestModal: FC<Props> = ({ balance, onClose, type }) => {
  const queryClient = useQueryClient();
  const alerts = useAlerts();

  const user = useUser();

  const remainingVacationDays = `${balance?.availableDays ?? '-'} ${balance?.availableDays === 1 ? 'day' : 'days'}`;

  const methods = useForm<FormDataT>({
    defaultValues: {
      freelancer_id: user?.freelancer?.id,
      request_date: new Date().toISOString().split('T')[0],
      type,
    },
  });

  const {
    control,
    handleSubmit,
    clearErrors,
    setError,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        await api.post<never, { id: number }>(
          '/v1/payments/freelancers/holidays/requests',
          data,
        );

        await queryClient.invalidateQueries(['freelancer_vacation_requests'], {
          exact: false,
        });

        await queryClient.invalidateQueries(['freelancer_vacation_balance'], {
          exact: false,
        });

        alerts.success(`Vacation request created successfully`);
        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, false, true);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel
        heading={`Request ${type === VacationRequestType.Paid ? 'Paid' : 'Unpaid'} time off`}
        onClose={onClose}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <RemainingDaysNotice>
              <Text $type="smallHeadline" $color="gray">
                Remaining{' '}
                <Text $type="smallHeadline" as="span">
                  Paid
                </Text>{' '}
                Vacation Days
              </Text>
              <Days $type="button">
                <Dot />
                {remainingVacationDays}
              </Days>
            </RemainingDaysNotice>
            <FormLayout>
              <FormField<FormDataT>
                control={control}
                name="request_date"
                type="text"
                label="Request Date"
                disabled
              />
              <FormField<FormDataT>
                control={control}
                name="start_date"
                type="datepicker"
                datepickerType="date"
                label="Date From"
                disableDayDimming
              />
              <FormField<FormDataT>
                control={control}
                name="end_date"
                type="datepicker"
                datepickerType="date"
                label="Date To"
                disableDayDimming
              />
              <FormField<FormDataT>
                control={control}
                name="notes"
                type="textarea"
                label="Comment"
                optional
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Submit
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Close
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default VacationRequestModal;
